import { ARMORY_UNLOCKS, canBuyUnlock } from "../meta/unlocks";
import type { SaveData } from "../meta/save";

// The Armory: permanent unlocks bought with ops points banked from runs.
// Renders the profile passed in; purchases go back through the callback so
// the flow owns the save mutation.

export class ArmoryScreen {
  private readonly el: HTMLElement;
  private save: SaveData | null = null;

  constructor(private readonly callbacks: {
    onBuy: (unlockId: string) => void;
    onBack: () => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "armory";
    this.el.className = "screen-overlay screen-overlay--stacked";
    this.el.style.zIndex = "350";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", this.handleClick);
  }

  setProfile(save: SaveData): void {
    this.save = save;
    this.render();
  }

  private render(): void {
    const save = this.save;
    if (!save) return;
    const owned = new Set(save.armoryUnlocks);

    const cards = ARMORY_UNLOCKS.map((unlock) => {
      const isOwned = owned.has(unlock.id);
      const canBuy = canBuyUnlock(unlock.id, owned, save.opsPoints);
      const state = isOwned ? "owned" : canBuy ? "open" : "locked";
      const action = isOwned
        ? `<span class="skill-row__status skill-row__status--active">OWNED</span>`
        : canBuy
          ? `<button class="screen-button screen-button--small" data-buy="${unlock.id}">${unlock.cost} OPS</button>`
          : `<span class="skill-row__status skill-row__status--muted">${unlock.cost} OPS</span>`;
      return `
        <div class="armory-card armory-card--${state}">
          <div class="armory-card__name">${unlock.name}</div>
          <div class="armory-card__desc">${unlock.desc}</div>
          <div class="armory-card__action">${action}</div>
        </div>`;
    }).join("");

    const c = save.career;
    const career = `RUNS WON ${c.runsCompleted} &nbsp;|&nbsp; RUNS LOST ${c.runsFailed} &nbsp;|&nbsp; BEST DEPTH ${c.bestDepth}/${5} &nbsp;|&nbsp; CAREER KILLS ${c.kills}`;

    this.el.innerHTML = `
      <div class="screen-kicker">PERMANENT REQUISITIONS</div>
      <div class="screen-title screen-title--compact">ARMORY</div>
      <div class="screen-subtitle">OPS POINTS: ${save.opsPoints}</div>
      <div class="armory-grid">${cards}</div>
      <div class="career-line">${career}</div>
      <div class="screen-actions" style="margin-top:12px">
        <button id="armory-back" class="screen-button">BACK</button>
      </div>
    `;
  }

  show(): void {
    this.render();
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("#armory-back")) {
      this.callbacks.onBack();
      return;
    }
    const unlockId = target.closest<HTMLElement>("[data-buy]")?.dataset["buy"];
    if (unlockId) this.callbacks.onBuy(unlockId);
  };
}
